import { createStyles, FormControl, FormControlLabel, IconButton, makeStyles, Radio, RadioGroup, Theme } from '@material-ui/core';
import React, { useEffect, useState } from 'react';
import { ChartCommonProps, IconButtonWrapper } from '../Common';
import { ChartFilter } from '../ChartComponents';
import { SearchCriteria } from '../SearchCriteria';
import CloseIcon from '@material-ui/icons/Close';
import { convertStringNumberToNumber, ETimePeriod, formatNumber, formatToDatePeriod, getLabels, getMaxTickLimit, getSuggestedMaxTick } from '../../helpers/ChartHelper';
import gql from 'graphql-tag';
import { useQuery } from 'react-apollo';
import { DrillDownType } from '../DrillDownType';
import { SelectedItemProps } from '../MattersByStatusChart';
import { Bar, getElementAtEvent } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, Scale, Tick, Chart, ChartEvent, ActiveElement } from 'chart.js';
import zoomPlugin from 'chartjs-plugin-zoom';
import { ChartInfo } from '../../components/ChartInfo';

import { RV_CHART_CLASS, RV_CHART_COLORS, isChartDataEmpty, rvBarDatasetStyle, rvChartAnimation, rvCategoryAxisStyle, rvTooltipStyle, rvValueAxisStyle } from '../chartTheme';
import { ChartHeading } from '../ChartHeading';
import { AnimatedNumber } from '../AnimatedNumber';
import { ChartSkeleton } from '../../components/loading/ChartSkeleton';
import { ChartEmptyState } from '../ChartEmptyState';
import { ChartNotes } from '../ChartNotes';
import ReceiptOutlinedIcon from '@material-ui/icons/ReceiptOutlined';
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, zoomPlugin);

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            position: 'relative',
            paddingBottom: '10px',
            flex: 1,
            display: 'flex',
            flexFlow: 'column',
            maxWidth: '100%',
            maxHeight: '100%',
            height: '100%',
            textAlign: 'center',
            '@media print': {
                display: 'flex',
                flexFlow: 'column',
                alignItems: 'center',
                boxShadow: 'none !important',
            },
        },
        popover: {},
        paper: {},
        radioGroup: {
            flexFlow: 'row wrap',
            justifyContent: 'center',
        },
        closeIcon: {
            color: 'rgb(0, 120, 212)',
        },
        titleContainer: {
            display: 'flex',
            flexFlow: 'column',
            padding: '20px 15px',
            '& .title': {
                fontSize: 16,
            },
            '& .colWrapper': {
                display: 'flex',
                justifyContent: 'center',
                margin: '0 -10px',
                flexFlow: 'row wrap'
            },
            '& .col': {
                flex: 1,
                margin: '0 10px 10px 10px',
                padding: 10,
                fontSize: 16,
                fontWeight: 600,
                textAlign: 'center',
                border: '1px solid',
                '& .value': {
                    fontSize: '2.5rem',
                    padding: '20px 0',
                },
            },
        },
        barContainer: {
            padding: '5px 15px 15px 15px',
            minHeight: '300px',
            flex: 1,
            display: 'flex',
            flexFlow: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            maxHeight: '350px',
            maxWidth: 'auto',
            position: 'relative',
            '@media (max-width: 533px)': {
                maxWidth: '350px',
                margin: '0 auto',
            },
            '& .titleWrapper': {
                textAlign: 'center',
                display: 'inline-block',
                width: '100%',
                padding: '15px',
                '& .title': {
                    fontSize: '18px',
                    fontWeight: 600,
                    display: 'block',
                    marginBottom: '15px',
                },
                '& .value': {
                    fontSize: '24px',
                    fontWeight: 600,
                    display: 'block',
                    marginBottom: '15px',
                },
                '& .secondary-title': {
                    fontSize: '16px',
                    fontWeight: 400,
                    display: 'block',
                    marginBottom: '15px',
                },
            },
        },
        loadingWrapper: {
            padding: '20px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
        },
        title: {
            fontSize: 16,
            fontWeight: 600,
            fontFamily: 'inherit',
            marginBottom: 10,
        },
        span: {
            fontSize: '1rem',
        },
        totalDiv: {
            cursor: 'pointer',
            paddingRight: 5,
        },
        helpIcon: {
            position: 'absolute',
            left: '5px',
            top: '5px',
            '@media print': {
                display: 'none !important',
            },
        },
    }),
);

export enum OptionsEnum {
    MONTH,
    PREVIOUS_MONTH,
    FINANCIAL_YEAR,
    PREV_FINANCIAL_YEAR,
}

interface FeesBilledPartChartProps extends SelectedItemProps, ChartCommonProps {
    chartFilter?: ChartFilter;
}

interface ChartState {
    options: OptionsEnum;
    months: string[];
    counts: number[];
    currentYearTotal: number;
    currentTotal: number;
    leftPeriodString: string;
    rightPeriodString: string;
    symbol: string;
}

export const FeesBilledPartChart: React.FC<FeesBilledPartChartProps> = (props) => {
    const classes = useStyles();

    // tslint:disable-next-line:no-any
    const chartRef = React.useRef<any>();

    const feesBilled = useQuery<FeesBilledQueryData>(FeesBilledQuery, {
        variables: {
            entityType: props.chartFilter?.entityType,
            entityGuid: props.chartFilter?.entityGuid,
            responsibility: props.chartFilter?.responsibility,
        },
    });

    const [state, setState] = useState<ChartState>({
        options: OptionsEnum.MONTH,
        months: [],
        counts: [],
        currentYearTotal: 0,
        currentTotal: 0,
        leftPeriodString: formatToDatePeriod('monthToDate'),
        rightPeriodString: formatToDatePeriod('yearToDate'),
        symbol: '$'
    });

    const currencySymbolQuery = useQuery<CurrencySymbolData>(CURRENCY_SYMBOL_QUERY);

    useEffect(() => {
        if (currencySymbolQuery.data && currencySymbolQuery.data.options && !currencySymbolQuery.loading && !currencySymbolQuery.error) {
            setState((prevState) => {
                return {
                    ...prevState,
                    symbol: currencySymbolQuery.data!.options.currencySymbol.symbol,
                };
            });
        }
        // tslint:disable-next-line: align
    }, [currencySymbolQuery.loading, currencySymbolQuery.error, currencySymbolQuery.data]);

    useEffect(() => {
        setState((prevState) => {
            let months: string[] = [];
            let counts: number[] = [];
            let currentTotal: number = 0;
            let currentYearTotal: number = 0;
            let leftPeriodString: string = '';
            let rightPeriodString: string = formatToDatePeriod('yearToDate');

            if (!feesBilled.error && feesBilled.data) {
                var lastMonth = feesBilled.data.statistics.fees.feesBilled.lastMonth;
                var previousMonth = feesBilled.data.statistics.fees.feesBilled.previousMonth;
                var lastYear = feesBilled.data.statistics.fees.feesBilled.lastFinancialYear;
                var prevYear = feesBilled.data.statistics.fees.feesBilled.previousFinancialYear;

                currentYearTotal = lastYear.summary.lastPeriodTotal;

                if (state.options === OptionsEnum.MONTH) {
                    months = lastMonth.feesBilledGrouping.map((s) => s.group);
                    counts = lastMonth.feesBilledGrouping.map((s) => s.value);
                    currentTotal = lastMonth.summary ? lastMonth.summary.lastPeriodTotal : 0;
                    leftPeriodString = formatToDatePeriod('monthToDate');
                } else if (state.options === OptionsEnum.PREVIOUS_MONTH) {
                    months = previousMonth.feesBilledGrouping.map((s) => s.group);
                    counts = previousMonth.feesBilledGrouping.map((s) => s.value);
                    currentTotal = previousMonth.summary ? previousMonth.summary.lastPeriodTotal : 0;

                    if (previousMonth.financialYearToPreviousMonthSummary) {
                        currentYearTotal = previousMonth.financialYearToPreviousMonthSummary.lastPeriodTotal;
                    } else {
                        currentYearTotal = 0;
                    }

                    leftPeriodString = formatToDatePeriod('previousMonth');
                    rightPeriodString = formatToDatePeriod('previousMonthYearToDate');
                } else if (state.options === OptionsEnum.FINANCIAL_YEAR) {
                    months = lastYear.feesBilledGrouping.map((s) => s.group);
                    counts = lastYear.feesBilledGrouping.map((s) => s.value);
                    currentTotal = lastYear.summary ? lastYear.summary.lastPeriodTotal : 0;
                    leftPeriodString = formatToDatePeriod('yearToDate');
                } else {
                    months = prevYear.feesBilledGrouping.map((s) => s.group);
                    counts = prevYear.feesBilledGrouping.map((s) => s.value);
                    currentTotal = prevYear.summary ? prevYear.summary.lastPeriodTotal : 0;
                    leftPeriodString = formatToDatePeriod('previousYearToDate');
                }
            }

            return {
                ...prevState,
                months,
                counts,
                currentTotal,
                currentYearTotal,
                leftPeriodString,
                rightPeriodString,
            };
        });
    // tslint:disable-next-line: align
    }, [state.options, feesBilled.loading]);

    const onCloseChart = () => {
        if (props.onCloseChart) {
            props.onCloseChart('isFeesBilledPartChartOpen');
        }
    };

    const onChangeRadio = (event: React.ChangeEvent<HTMLInputElement>) => {
        setState((prevState) => {
            return {
                ...prevState,
                // tslint:disable-next-line: radix
                options: parseInt((event.target as HTMLInputElement).value),
            };
        });
    };

    const handleTotalClick = (isMonth: Boolean) => {
        if (props.getSelectedItem) {
            const searchCriterias = new Array<SearchCriteria>();
            let defaultView = 'Fees Billed MTD/YTD';
            let timePeriod = ETimePeriod.THIS_MONTH;

            if (isMonth) {
                if (state.options === OptionsEnum.MONTH) {
                    searchCriterias.push({ property: 'Month', operator: '', comparison: '', period: 'curr' });
                    timePeriod = ETimePeriod.THIS_MONTH;
                } else if (state.options === OptionsEnum.PREVIOUS_MONTH) {
                    searchCriterias.push({ property: 'Month', operator: '', comparison: '', period: 'prev' });
                    defaultView = 'Fees Billed PM/PYTD';
                    timePeriod = ETimePeriod.PREVIOUS_MONTH;
                } else if (state.options === OptionsEnum.FINANCIAL_YEAR) {
                    searchCriterias.push({ property: 'FinancialYearToDate', operator: '', comparison: '', period: 'curr' });
                    timePeriod = ETimePeriod.FINANCIAL_YEAR;
                } else if (state.options === OptionsEnum.PREV_FINANCIAL_YEAR) {
                    searchCriterias.push({ property: 'FinancialYearToDate', operator: '', comparison: '', period: 'prev' });
                    defaultView = 'Fees Billed PM/PYTD';
                    timePeriod = ETimePeriod.PREVIOUS_FINANCIAL_YEAR;
                }
            } else {
                var period = 'curr';
                timePeriod = ETimePeriod.FINANCIAL_YEAR;
                if (state.options === OptionsEnum.PREVIOUS_MONTH) {
                    period = 'prevMonth';
                    defaultView = 'Fees Billed PM/PYTD';
                    timePeriod = ETimePeriod.PREVIOUS_MONTH;
                }

                searchCriterias.push({ property: 'FinancialYearToDate', operator: '', comparison: '', period: period });
            }

            const title = `Drilldown from Fees Billed for ${timePeriod}`;

            props.getSelectedItem(searchCriterias, false, title, DrillDownType.staffBillingSummary, defaultView);
        }
    };

    const onPan = (context: { chart: Chart }) => {
        chartRef.current.options.scales.y.min = 0;
        chartRef.current.update();
    };

    const onHover = (event: ChartEvent, elements: ActiveElement[], chart: Chart) => {
        (event?.native?.target as HTMLElement).style.cursor = elements.length > 0 ? 'pointer' : 'auto';
    };

    if (currencySymbolQuery.loading || feesBilled.loading) {
        return (
            <ChartSkeleton variant="columns" />
        );
    }

    return (
        <div className={`${classes.root} ${RV_CHART_CLASS}${isChartDataEmpty(state.counts) ? ' rv-chart--empty' : ''}`}>
            <IconButtonWrapper aria-label="close">
                <ChartInfo 
                    description={props.description}
                    fontSize={'medium'}
                    className={`${classes.helpIcon} rv-chart-help`}
                />
                <IconButton aria-label="close" onClick={onCloseChart} size="small">
                    <CloseIcon className={classes.closeIcon} />
                </IconButton>
            </IconButtonWrapper>
            <div className={classes.titleContainer}>
                <ChartHeading icon={<ReceiptOutlinedIcon />} title="Fees Billed" />

                <div className="colWrapper">
                    <div className="col">
                        <div className="title">{state.leftPeriodString}</div>
                        <div className={`${classes.totalDiv} value`} onClick={() => handleTotalClick(true)}>
                            <AnimatedNumber value={state.currentTotal} format={(amount) => `${state.symbol}${formatNumber(amount)}`} />
                        </div>
                    </div>
                    <div className="col">
                        <div className="title">{state.rightPeriodString}</div>
                        <div className={`${classes.totalDiv} value`} onClick={() => handleTotalClick(false)}>
                            <AnimatedNumber value={state.currentYearTotal} format={(amount) => `${state.symbol}${formatNumber(amount)}`} />
                        </div>
                    </div>
                </div>
            </div>
            <div className={classes.barContainer}>
                <Bar
                    ref={chartRef}
                    // tslint:disable-next-line: no-any
                    onClick={(event: React.MouseEvent<any>) => {
                        const _data = getElementAtEvent(chartRef.current, event);
                        if (_data.length > 0 && props.getSelectedItem) {
                            // const _index = getElementAtEvent(chartRef.current, event)[0].index;

                            // const item = state.months[_index];

                            // const searchCriterias = new Array<SearchCriteria>();

                            // if (state.options === OptionsEnum.MONTH || state.options === OptionsEnum.PREVIOUS_MONTH) {
                            //     searchCriterias.push(
                            //         { property: 'DateShortString', operator: 'eq', comparison: item, period: 'curr' }, // e.g. May 05
                            //     );
                            // } else if (state.options === OptionsEnum.FINANCIAL_YEAR) {
                            //     searchCriterias.push(
                            //         { property: 'Month', operator: 'eq', comparison: item, period: 'curr' }, // e.g. May
                            //     );
                            // } else {
                            //     searchCriterias.push(
                            //         { property: 'Month', operator: 'eq', comparison: item, period: 'prev' }
                            //     );
                            // }

                            // props.getSelectedItem(
                            //     searchCriterias,
                            //     false,
                            //     'Fees Billed - ' + item,
                            //     DrillDownType.staffBillingSummary,
                            //     state.options === OptionsEnum.MONTH
                            //         ? 'Fees Billed WTD'
                            //         : 'Fees Billed MTD/YTD'
                            // );
                        }
                    }}
                    data={{
                        labels: getLabels(state.months),
                        datasets: [
                            {
                                fill: false,
                                label: 'Fees Billed',
                                ...rvBarDatasetStyle(RV_CHART_COLORS.primary),
                                data: state.counts,
                                type: 'bar',
                            },
                        // tslint:disable-next-line:no-any
                        ] as any,
                    }}
                    options={{
                        onHover: onHover,
                        indexAxis: 'x' as const,
                        animation: rvChartAnimation(),
                        elements: { bar: { borderWidth: 0 } },
                        responsive: true,
                        maintainAspectRatio: false,
                        scales: {
                            y: {
                                grid: rvValueAxisStyle.grid,
                                // beginAtZero: true,
                                suggestedMax: getSuggestedMaxTick(state.counts),
                                beginAtZero: true,
                                ticks: {
                                    ...rvValueAxisStyle.ticks,
                                    maxTicksLimit: getMaxTickLimit(state.counts),
                                    autoSkip: false,
                                    callback: function (this: Scale, tickValue: number, index: number, ticks: Tick[]) {
                                        return `${state.symbol}${formatNumber(tickValue)}`;
                                    },
                                },
                            },
                            x: {
                                grid: rvCategoryAxisStyle.grid,
                                // min: 0,
                                // max: 9 // minimum display
                                ticks: {
                                    ...rvCategoryAxisStyle.ticks,
                                    autoSkip: false
                                }
                            }
                        },
                        plugins: {
                            legend: {
                                position: 'bottom' as const,
                                display: false,
                            },
                            title: {
                                display: false,
                                text: ``,
                            },
                            zoom: {
                                pan: {
                                    enabled: false,
                                    onPan: onPan,
                                },
                                zoom: {
                                    wheel: {
                                        enabled: false,
                                    },
                                },
                                // limits: {
                                //   y: { min: 0, max: 9 },
                                // },
                            },
                            tooltip: {
                                ...rvTooltipStyle,
                                callbacks: {
                                    // tslint:disable-next-line: no-any
                                    label: function (context: any) {
                                        const {
                                            //   dataIndex,
                                            //   label,
                                            formattedValue,
                                            dataset,
                                        } = context;

                                        var currentValue = convertStringNumberToNumber(formattedValue);

                                        return `${dataset.label}: ${state.symbol}${formatNumber(currentValue)}`;
                                    },
                                },
                            },
                        },
                    }}
                />
                {isChartDataEmpty(state.counts) && (
                    <ChartEmptyState title="No data" hint="Nothing to show for this selection." />
                )}
            </div>
            <FormControl component="div">
                <RadioGroup name="last" value={state.options} onChange={onChangeRadio} className={classes.radioGroup}>
                    <FormControlLabel value={OptionsEnum.MONTH} control={<Radio color="primary" />} label="This Month" />
                    <FormControlLabel value={OptionsEnum.PREVIOUS_MONTH} control={<Radio color="primary" />} label="Previous Month" />
                    <FormControlLabel value={OptionsEnum.FINANCIAL_YEAR} control={<Radio color="primary" />} label="Financial Year" />
                    <FormControlLabel value={OptionsEnum.PREV_FINANCIAL_YEAR} control={<Radio color="primary" />} label="Previous Financial Year" />
                </RadioGroup>
            </FormControl>
            <ChartNotes notes={['Information collated based on Timekeeper']} actionHint="Drill down to timekeeper details" />
        </div>
    );
};

// tslint:disable-next-line:no-any

export interface CurrencySymbolData {
    options: Options;
}

export interface Options {
    currencySymbol: CurrencySymbol;
}

export interface CurrencySymbol {
    symbol: string;
}

export const CURRENCY_SYMBOL_QUERY = gql`
    query CurrencySymbol {
        options {
            currencySymbol {
                symbol
            }
        }
    }
`;

export interface FeesBilledQueryParams {
    entityType: number | null;
    entityGuid: string | null;
    responsibility: number | null;
}
export interface FeesBilledQueryData {
    statistics: Statistics;
}

export interface Statistics {
    fees: Fees;
}

export interface Fees {
    feesBilled: FeesBilled;
}

export interface FeesBilled {
    lastFinancialYear: LastFinancialYear;
    lastMonth: LastFinancialYear;
    previousMonth: LastFinancialYear;
    previousFinancialYear: LastFinancialYear;
}

export interface LastFinancialYear {
    summary: Summary;
    financialYearToPreviousMonthSummary: Summary;
    feesBilledGrouping: FeesBilledGrouping[];
}

export interface FeesBilledGrouping {
    group: string;
    value: number;
}

export interface Summary {
    lastPeriodTotal: number;
    previousPeriodTotal: number;
}

const FeesBilledQuery = gql`
    query FeesBilledData($entityType: Int, $entityGuid: String, $responsibility: Int) {
        statistics {
            fees {
                feesBilled(entityType: $entityType, entityGuid: $entityGuid, responsibility: $responsibility) {
                    lastFinancialYear {
                        summary {
                            lastPeriodTotal
                            previousPeriodTotal
                        }
                        feesBilledGrouping {
                            group
                            value
                        }
                    }
                    lastMonth {
                        summary {
                            lastPeriodTotal
                            previousPeriodTotal
                        }
                        feesBilledGrouping {
                            group
                            value
                        }
                    }
                    previousMonth {
                        summary {
                            lastPeriodTotal
                            previousPeriodTotal
                        }
                        financialYearToPreviousMonthSummary {
                            lastPeriodTotal
                            previousPeriodTotal
                        }
                        feesBilledGrouping {
                            group
                            value
                        }
                    }
                    previousFinancialYear {
                        summary {
                            lastPeriodTotal
                            previousPeriodTotal
                        }
                        feesBilledGrouping {
                            group
                            value
                        }
                    }
                }
            }
        }
    }
`;
